import { parseDocumentIdentifier } from "@wiki/core";

import type { SharedTreeNode } from "../../api/shares";

/**
 * Mapping a public body's links into the share's URL space
 * (`docs/spec/shares.md` §3.2, item 4).
 *
 * The server leaves internal paths alone — the recorded `sharedTree` keeps them — so this is
 * where they are resolved. A link to a document the share exposes becomes
 * `/s/<shareId>/doc/<slug>`; a link to anything else loses its href entirely, so no internal
 * path is ever clickable from a public page. Mentions render as plain text for the same reason.
 */

/** Every document the share exposes, indexed by both the ids a link can name. */
export interface ShareLinkIndex {
  shareId: string;
  paths: Map<string, string>;
}

function indexNode(node: SharedTreeNode, shareId: string, paths: Map<string, string>): void {
  const inShare = node.url.startsWith("/doc/") ? `/s/${shareId}${node.url}` : null;
  if (inShare !== null) {
    paths.set(node.id, inShare);
    const identifier = parseDocumentIdentifier(node.url.slice("/doc/".length));
    if (identifier) paths.set(identifier.value, inShare);
  }
  for (const child of node.children) indexNode(child, shareId, paths);
}

export function shareLinkIndex(shareId: string, tree: SharedTreeNode | null): ShareLinkIndex {
  const paths = new Map<string, string>();
  if (tree) indexNode(tree, shareId, paths);
  return { shareId, paths };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function stripOrigin(href: string): string {
  const match = /^https?:\/\/[^/]+(\/.*)$/.exec(href);
  return match?.[1] ?? href;
}

/** Whether a href points inside this installation at all. */
function internalPath(href: string): string | null {
  const path = stripOrigin(href);
  return path.startsWith("/doc/") || path.startsWith("/collection/") || path.startsWith("/s/")
    ? path
    : null;
}

/** The share-space path for an internal link, or null when the target is out of the share. */
export function mapHref(href: string, index: ShareLinkIndex): string | null {
  const path = internalPath(href);
  if (path === null) {
    // Not one of ours: an external link stays exactly as the author wrote it.
    return href;
  }
  if (path.startsWith(`/s/${index.shareId}/`)) {
    return path;
  }
  if (!path.startsWith("/doc/")) {
    return null;
  }
  const [slug] = path.slice("/doc/".length).split(/[?#]/);
  const identifier = parseDocumentIdentifier(decodeURIComponent(slug ?? ""));
  if (!identifier) return null;
  return index.paths.get(identifier.value) ?? null;
}

/**
 * Rewrites a rich-text body in place of its copy: link marks are remapped or dropped, and
 * mention nodes collapse to their label.
 */
export function mapShareLinks(
  content: Record<string, unknown>,
  index: ShareLinkIndex,
): Record<string, unknown> {
  const copy = structuredClone(content);

  const visitList = (nodes: unknown[]): unknown[] =>
    nodes.map((node) => visit(node)).filter((node) => node !== null);

  const visit = (node: unknown): unknown => {
    if (!isRecord(node)) return node;
    if (node.type === "mention") {
      const attrs = isRecord(node.attrs) ? node.attrs : {};
      const label = typeof attrs.label === "string" ? attrs.label : "";
      return label === "" ? null : { type: "text", text: label };
    }
    if (Array.isArray(node.marks)) {
      node.marks = (node.marks as unknown[])
        .map((mark): unknown => {
          if (!isRecord(mark) || mark.type !== "link" || !isRecord(mark.attrs)) return mark;
          const href = typeof mark.attrs.href === "string" ? mark.attrs.href : "";
          const mapped = mapHref(href, index);
          if (mapped === null) return null;
          return { ...mark, attrs: { ...mark.attrs, href: mapped } };
        })
        .filter((mark) => mark !== null);
    }
    if (Array.isArray(node.content)) {
      node.content = visitList(node.content as unknown[]);
    }
    return node;
  };

  visit(copy);
  return copy;
}
